/**
 * ObservedSkillsTable — the "Observed" tab of the Skills page (O-769).
 *
 * Reads the tenant-wide ClickHouse rollup (ListObservedSkills) of skills the
 * agent has actually seen on endpoints, with a 5-way provenance badge. Rows
 * sharing a `(vendor, name)` key fold into a collapsible group so distinct
 * SKILL.md bodies under the same name read as sibling "variants".
 *
 * Admin-only on the server; the parent only mounts this for admins, but we
 * guard defensively too.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { NOTE_CLASS } from "../../lib/ui.js";
import { listObservedSkills } from "../../lib/skillsClient.js";
import { groupByName } from "./grouping.js";
import {
    OriginBadge,
    VendorBadge,
    AutoBadge,
    descriptionPrefix,
    shortSha,
    formatRelativeTime,
} from "./skillBadges.jsx";

const PAGE_SIZE = 25;

const TH_CLASS =
    "text-left text-[9px] uppercase tracking-[0.08em] font-medium text-[color:var(--muted-soft)] px-[8px] py-[5px]";
const TD_CLASS =
    "px-[8px] py-[6px] align-top text-[11px] leading-[1.32] text-[color:var(--ink)] border-t border-[color:var(--line-soft)]";

// Latest non-empty lastInvokedAt across a group's variants (for the header row).
function latestInvoked(rows) {
    let best = null;
    for (const r of rows) {
        const ts = r.lastInvokedAt ?? r.last_invoked_at;
        if (ts && (!best || ts > best)) best = ts;
    }
    return best;
}

function VariantLabel({ row }) {
    const label = row.displayDisambiguator ?? row.display_disambiguator;
    const isAuto = row.displayDisambiguatorIsAuto ?? row.display_disambiguator_is_auto;
    const sha = row.contentSha256 ?? row.content_sha256;
    const desc = row.description ?? "";
    return (
        <div className="grid gap-[2px] min-w-0">
            <span className="flex items-center gap-[6px] min-w-0">
                <span className="font-mono text-[11px] text-[color:var(--ink)] truncate">
                    {label || shortSha(sha)}
                </span>
                {isAuto ? <AutoBadge /> : null}
            </span>
            {desc ? (
                <span className={`${NOTE_CLASS} text-[10px] truncate`} title={desc}>
                    {descriptionPrefix(desc, 60)}
                </span>
            ) : null}
        </div>
    );
}

function dataCells(row) {
    const endpointCount = row.endpointCount ?? row.endpoint_count ?? 0;
    const invocations = row.invocationCount ?? row.invocation_count ?? 0;
    const lastInvoked = row.lastInvokedAt ?? row.last_invoked_at;
    const user =
        row.attributedUserDisplayName ?? row.attributed_user_display_name ?? "";
    const userEmail = row.attributedUserEmail ?? row.attributed_user_email ?? "";
    return (
        <>
            <td className={TD_CLASS}>
                <OriginBadge
                    origin={row.origin}
                    pluginId={row.pluginId ?? row.plugin_id}
                />
            </td>
            <td className={`${TD_CLASS} text-right tabular-nums text-[color:var(--muted)]`}>
                {Number(endpointCount).toLocaleString()}
            </td>
            <td className={`${TD_CLASS} text-right tabular-nums text-[color:var(--muted)]`}>
                {Number(invocations).toLocaleString()}
            </td>
            <td className={`${TD_CLASS} text-[color:var(--muted)] whitespace-nowrap`}>
                {formatRelativeTime(lastInvoked)}
            </td>
            <td className={`${TD_CLASS} text-[color:var(--muted)] truncate max-w-[160px]`}>
                {user ? <span title={userEmail || user}>{user}</span> : "—"}
            </td>
        </>
    );
}

export default function ObservedSkillsTable({ query, origin, vendor, contentSha256, isAdmin }) {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [expanded, setExpanded] = useState(() => new Set());

    // Reset to the first page whenever the filter set changes.
    useEffect(() => { setPage(0); }, [query, origin, vendor, contentSha256]);

    useEffect(() => {
        if (!isAdmin) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        listObservedSkills({ query, origin, vendor, contentSha256, page, pageSize: PAGE_SIZE })
            .then((resp) => {
                if (cancelled) return;
                setRows(Array.isArray(resp?.skills) ? resp.skills : []);
                setTotal(Number(resp?.total ?? 0));
            })
            .catch((err) => { if (!cancelled) setError(err.message || String(err)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [isAdmin, query, origin, vendor, contentSha256, page]);

    const groups = useMemo(
        () => groupByName(rows, (r) => `${r.vendor}|${r.skillName ?? r.skill_name}`),
        [rows],
    );

    const toggle = useCallback((key) => {
        setExpanded((cur) => {
            const next = new Set(cur);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    if (!isAdmin) {
        return <p className={`${NOTE_CLASS} px-[8px] py-[12px]`}>Observed skills are visible to tenant admins only.</p>;
    }

    const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

    return (
        <div className="grid gap-[8px]">
            <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[3px] text-[11px] text-[color:var(--muted)]">
                <span>
                    <strong className="text-[color:var(--ink)]">{total.toLocaleString()}</strong>{" "}
                    observed {total === 1 ? "skill" : "skills"}
                </span>
                {contentSha256 ? (
                    <span className={`${NOTE_CLASS} font-mono text-[10px]`} title={contentSha256}>
                        · filtered by content {shortSha(contentSha256)}
                    </span>
                ) : null}
                {loading ? <span className={NOTE_CLASS}>Loading…</span> : null}
                {error ? <span className="text-red-400 truncate max-w-[400px]" title={error}>{error}</span> : null}
            </div>

            {!loading && !error && rows.length === 0 ? (
                <div className="grid gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[12px]">
                    <strong className="text-[13px] leading-[1.08] font-medium">No observed skills yet</strong>
                    <span className={NOTE_CLASS}>
                        Skills appear here once an agent reports a <code>SKILL.md</code> on disk from a monitored endpoint.
                    </span>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr>
                                <th className={TH_CLASS}>Name</th>
                                <th className={TH_CLASS}>Vendor</th>
                                <th className={TH_CLASS}>Provenance</th>
                                <th className={`${TH_CLASS} text-right`}>Endpoints</th>
                                <th className={`${TH_CLASS} text-right`}>Invocations (90d)</th>
                                <th className={TH_CLASS}>Last invoked</th>
                                <th className={TH_CLASS}>Attributed user</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groups.map((g) => {
                                const name = g.rows[0].skillName ?? g.rows[0].skill_name;
                                const vendorVal = g.rows[0].vendor;
                                if (g.rows.length === 1) {
                                    const row = g.rows[0];
                                    const desc = row.description ?? "";
                                    return (
                                        <tr key={g.key}>
                                            <td className={TD_CLASS}>
                                                <div className="grid gap-[2px] min-w-0">
                                                    <strong className="text-[11px] font-medium truncate">{name}</strong>
                                                    {desc ? (
                                                        <span className={`${NOTE_CLASS} text-[10px] truncate`} title={desc}>
                                                            {descriptionPrefix(desc, 60)}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </td>
                                            <td className={TD_CLASS}><VendorBadge vendor={vendorVal} /></td>
                                            {dataCells(row)}
                                        </tr>
                                    );
                                }
                                const open = expanded.has(g.key);
                                return (
                                    <Fragment key={g.key}>
                                        <tr
                                            onClick={() => toggle(g.key)}
                                            className="cursor-pointer hover:bg-[color:var(--control-bg)]"
                                        >
                                            <td className={TD_CLASS}>
                                                <span className="flex items-center gap-[6px] min-w-0">
                                                    <span className="text-[color:var(--muted)] text-[10px] w-[10px]">
                                                        {open ? "▾" : "▸"}
                                                    </span>
                                                    <strong className="text-[11px] font-medium truncate">{name}</strong>
                                                    <span className="inline-flex items-center rounded-[2px] border border-[color:var(--line)] px-[5px] py-[1px] text-[9px] uppercase tracking-[0.08em] font-medium text-[color:var(--muted)]">
                                                        {g.rows.length} variants
                                                    </span>
                                                </span>
                                            </td>
                                            <td className={TD_CLASS}><VendorBadge vendor={vendorVal} /></td>
                                            <td className={`${TD_CLASS} text-[color:var(--muted-soft)]`}>—</td>
                                            <td className={`${TD_CLASS} text-right text-[color:var(--muted-soft)]`}>—</td>
                                            <td className={`${TD_CLASS} text-right text-[color:var(--muted-soft)]`}>—</td>
                                            <td className={`${TD_CLASS} text-[color:var(--muted)] whitespace-nowrap`}>
                                                {formatRelativeTime(latestInvoked(g.rows))}
                                            </td>
                                            <td className={`${TD_CLASS} text-[color:var(--muted-soft)]`}>—</td>
                                        </tr>
                                        {open
                                            ? g.rows.map((row) => (
                                                  <tr key={`${g.key}|${row.contentSha256 ?? row.content_sha256}`} className="bg-[color:var(--control-bg)]/40">
                                                      <td className={`${TD_CLASS} pl-[28px]`}>
                                                          <VariantLabel row={row} />
                                                      </td>
                                                      <td className={TD_CLASS}><VendorBadge vendor={row.vendor} /></td>
                                                      {dataCells(row)}
                                                  </tr>
                                              ))
                                            : null}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {total > PAGE_SIZE ? (
                <div className="flex items-center justify-end gap-[8px] text-[11px] text-[color:var(--muted)]">
                    <span className={NOTE_CLASS}>Page {page + 1} of {lastPage + 1}</span>
                    <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        disabled={page === 0 || loading}
                        className="h-[26px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-40 cursor-pointer"
                    >
                        Prev
                    </button>
                    <button
                        type="button"
                        onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                        disabled={page >= lastPage || loading}
                        className="h-[26px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-40 cursor-pointer"
                    >
                        Next
                    </button>
                </div>
            ) : null}
        </div>
    );
}
